import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { z } from "zod";

export type WhiteLabelContext = {
  company: WhiteLabelCompany;
};

export const whiteLabelCompanies = [
  "default",
  "company-1",
  "company-2",
] as const;

const WHITE_LABEL_COMPANY_SCHEMA = z.enum(whiteLabelCompanies).catch("default");

export type WhiteLabelCompany = (typeof whiteLabelCompanies)[number];

const WhiteLabelContext = createContext<WhiteLabelContext | null>(null);

export const useWhiteLabel = () => {
  const ctx = useContext(WhiteLabelContext);

  if (!ctx) {
    throw new Error("WhiteLabelContext must be used within the provider");
  }

  return ctx;
};

type WhiteLabelProviderProps = PropsWithChildren;
const WHITE_LABEL_COMPANY_KEY = "energy-savings-white-label-company";

const getCompanyFromLocalStorage = () => {
  const localStorageCompany = localStorage.getItem(WHITE_LABEL_COMPANY_KEY);
  const parsedCompany = WHITE_LABEL_COMPANY_SCHEMA.parse(localStorageCompany);

  if (localStorageCompany !== parsedCompany) {
    localStorage.setItem(WHITE_LABEL_COMPANY_KEY, parsedCompany);
  }

  return parsedCompany;
};

export const WhiteLabelProvider = ({ children }: WhiteLabelProviderProps) => {
  const [company, setCompany] = useState(getCompanyFromLocalStorage());

  useEffect(() => {
    const onStorageChange = (event: StorageEvent) => {
      if (event.key === WHITE_LABEL_COMPANY_KEY) {
        const newCompany = WHITE_LABEL_COMPANY_SCHEMA.parse(event.newValue);
        setCompany(newCompany);
      }
    };

    window.addEventListener("storage", onStorageChange);

    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  const value = useMemo(() => ({ company }), [company]);

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};
